import _ from 'underscore';
import Promise from 'bluebird';
import { useEffect, useMemo, useState, useCallback } from 'react';

const PREFERENCE_NAME = 'AdsStudioPreference';

const usePerference = ({ preferenceService, getterName, setterName }) => {
  const [preferenceValue, setPreferenceValue] = useState(null);

  useEffect(() => {
    // wait until preferenceService is initialized
    if (preferenceService && _.isFunction(preferenceService[getterName])) {
      const pref = preferenceService[getterName](PREFERENCE_NAME);
      const curValue = pref && !_.isUndefined(pref.Data) ? pref.Data : null;
      setPreferenceValue(curValue || {});
    }
  }, [getterName, preferenceService]);

  const updatePreference = useCallback((newPreferenceValue) => {
    if (preferenceService && _.isFunction(preferenceService[setterName])) {
      if (!_.isMatch(preferenceValue, newPreferenceValue)) {
        const data = { ...preferenceValue || {}, ...newPreferenceValue };
        setPreferenceValue(data);
        return preferenceService[setterName]({
          Name: PREFERENCE_NAME,
          Data: data,
        });
      }
      return Promise.resolve(preferenceValue);
    }
    return Promise.reject(new Error('preference service is not ready.'));
  }, [preferenceService, setterName, preferenceValue]);

  const preferences = useMemo(() => {
    if (!preferenceValue) {
      return null;
    }

    // sort by
    const { sortBy } = preferenceValue || {};
    const setSortBy = (newSortBy) => {
      updatePreference({ sortBy: newSortBy });
    };

    // add assets teaching bubble
    // show at most 3 times, if users clicked any button, only shown once
    let { addFoldersTeachingBubbleAllowedShowCount } = preferenceValue || {};
    if (!_.isNumber(addFoldersTeachingBubbleAllowedShowCount)) {
      addFoldersTeachingBubbleAllowedShowCount = 3;
    }
    const hideAddFoldersTeachingBubble = () => {
      if (addFoldersTeachingBubbleAllowedShowCount > 0) {
        addFoldersTeachingBubbleAllowedShowCount -= 1;
        updatePreference({ addFoldersTeachingBubbleAllowedShowCount });
      }
    };
    const dismissAddFoldersTeachingBubble = () => {
      updatePreference({ addFoldersTeachingBubbleAllowedShowCount: 0 });
    };

    // real user for asset library app
    const { realUsedFlagInAdsStudioApp } = preferenceValue || {};
    const setRealUsedFlagInAdsStudioApp = () => {
      const realUsedFlag = {
        haveUsed: true,
        timestamp: new Date().getTime(),
      };
      updatePreference({ realUsedFlagInAdsStudioApp: realUsedFlag });
    };

    // real user for asset library picker
    const { realUsedFlagInAdsStudioPicker } = preferenceValue || {};
    const setRealUsedFlagInAdsStudioPicker = () => {
      const realUsedFlag = {
        haveUsed: true,
        timestamp: new Date().getTime(),
      };
      updatePreference({ realUsedFlagInAdsStudioPicker: realUsedFlag });
    };

    // real user for asset library picker
    const { realUsedFlagInAdsStudioAIGCMVPView } = preferenceValue || {};
    const setRealUsedFlagInAdsStudioAIGCMVPView = () => {
      const realUsedFlag = {
        haveUsed: true,
        timestamp: new Date().getTime(),
      };
      updatePreference({ realUsedFlagInAdsStudioAIGCMVPView: realUsedFlag });
    };

    // performance table view preferences
    const { perfTablePageSize } = preferenceValue || {};
    const setPerfTablePageSize = (newValue) => {
      updatePreference({ perfTablePageSize: newValue });
    };

    const { perfTableSortBy } = preferenceValue || {};
    const setPerfTableSortBy = (newValue) => {
      updatePreference({ perfTableSortBy: newValue });
    };

    // left navigation collapsed/expanded
    const { pageMenuMode } = preferenceValue || {};
    const setPageMenuMode = (newValue) => {
      updatePreference({ pageMenuMode: newValue });
    };

    return ({
      sortBy,
      setSortBy,
      // add folder teaching bubble
      addFoldersTeachingBubbleAllowedShowCount,
      dismissAddFoldersTeachingBubble,
      hideAddFoldersTeachingBubble,
      // real user for asset library
      realUsedFlagInAdsStudioApp,
      setRealUsedFlagInAdsStudioApp,
      realUsedFlagInAdsStudioPicker,
      setRealUsedFlagInAdsStudioPicker,
      realUsedFlagInAdsStudioAIGCMVPView,
      setRealUsedFlagInAdsStudioAIGCMVPView,
      perfTablePageSize,
      setPerfTablePageSize,
      perfTableSortBy,
      setPerfTableSortBy,
      pageMenuMode,
      setPageMenuMode,
    });
  }, [preferenceValue, updatePreference]);

  return preferences;
};

export const useAdsStudioAccountPreference = ({ preferenceService }) => usePerference({
  preferenceService,
  getterName: 'findByNameAtAccountLevel',
  setterName: 'setAtAccountLevel',
});

export const useAdsStudioUserPreference = ({ preferenceService }) => usePerference({
  preferenceService,
  getterName: 'findByNameAtUserLevel',
  setterName: 'setAtUserLevel',
});

export const useAdsStudioPreference = ({ preferenceService }) => {
  const userPreference = useAdsStudioUserPreference({ preferenceService });
  const accountPreference = useAdsStudioAccountPreference({ preferenceService });
  return {
    user: userPreference,
    account: accountPreference,
  };
};
